import { RoadrunnerFigure, a11yProps, palette as P } from './RoadrunnerMark';

type Props = { className?: string; title?: string };

// Folded map: four panels with a gentle zig-zag along the folds.
const TOP: [number, number][] = [
  [60, 122],
  [104, 117],
  [148, 122],
  [192, 117],
  [236, 122],
];
const BOTTOM: [number, number][] = [
  [36, 166],
  [91.5, 161],
  [147, 166],
  [202.5, 161],
  [258, 166],
];

function panel(i: number) {
  const a = TOP[i]!;
  const b = TOP[i + 1]!;
  const c = BOTTOM[i + 1]!;
  const d = BOTTOM[i]!;
  return `M${a[0]} ${a[1]} L${b[0]} ${b[1]} L${c[0]} ${c[1]} L${d[0]} ${d[1]} Z`;
}

/** Location check: the roadrunner beside a map pin planted on a folded paper map. */
export function MapMarker({ className, title }: Props) {
  const line = {
    stroke: P.ink,
    strokeWidth: 2.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 280 180"
      width="100%"
      className={className}
      style={{ maxWidth: 320, display: 'block' }}
      {...a11yProps(title)}
    >
      {title && <title>{title}</title>}
      <g transform="translate(0 -9)">
        <circle cx={190} cy={88} r={40} fill={P.skySoft} />

        {/* map panels, alternating light so the folds read */}
        {[0, 1, 2, 3].map((i) => (
          <path key={i} d={panel(i)} fill={i % 2 ? P.paper : P.card} />
        ))}
        {/* map content */}
        <ellipse cx={66} cy={151} rx={13} ry={5.5} fill={P.skySoft} stroke={P.sky} strokeWidth={1.4} />
        <path d="M109 126 L132 127.4 L130 142 L106 140.6 Z" fill={P.sageSoft} />
        <path d="M160 146 L186 143.6 L189 157 L158 159.6 Z" fill={P.sageSoft} />
        <path d="M214 147 L238 149 L243 158 L217 157 Z" fill={P.sageSoft} />
        <path
          d="M198 124 C210 130 204 138 218 140 S236 142 246 150"
          fill="none"
          stroke={P.sky}
          strokeWidth={2.6}
          strokeLinecap="round"
          opacity={0.8}
        />
        <path
          d="M48 138 C80 130 112 154 146 143 S204 124 244 136"
          fill="none"
          stroke={P.adobe}
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeDasharray="6 6"
        />
        {/* folds and outline */}
        <path
          d="M104 117 L91.5 161 M148 122 L147 166 M192 117 L202.5 161"
          fill="none"
          stroke={P.inkSoft}
          strokeWidth={1.4}
          strokeLinecap="round"
          opacity={0.7}
        />
        <path
          d={`M${TOP.map((p) => p.join(' ')).join(' L')} L${[...BOTTOM]
            .reverse()
            .map((p) => p.join(' '))
            .join(' L')} Z`}
          fill="none"
          {...line}
        />

        {/* the pin */}
        <ellipse cx={190} cy={143} rx={9} ry={2.6} fill={P.ink} opacity={0.18} />
        <path
          d="M190 142 C184 128 172 114 172 100 C172 90 180 82 190 82 C200 82 208 90 208 100 C208 114 196 128 190 142 Z"
          fill={P.adobe}
          {...line}
        />
        <path
          d="M178.6 96 C179.6 91 183 88 187 87.4"
          fill="none"
          stroke={P.card}
          strokeWidth={2}
          strokeLinecap="round"
          opacity={0.5}
        />
        <circle cx={190} cy={100} r={6.6} fill={P.card} {...line} strokeWidth={2} />

        {/* the roadrunner, checking the spot */}
        <RoadrunnerFigure transform="translate(50 60.5) scale(1.5)" detailed />
      </g>
    </svg>
  );
}
